/** Support Inbox v2.5. The supplied offline HTML owns geometry and colors;
 * typography, authentication, RLS and delivery remain application-owned.
 * URL state preserves legacy view/ticket links and adds group/filter/state.
 */
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useMemo,
} from "react";
import { useLocation } from "react-router-dom";
import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, Search } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { useAdminRoster, adminDisplayName } from "@/hooks/useAdminRoster";
import { supportRosterLabel } from "./supportRosterLabel";
import { useSupportInbox } from "@/hooks/useSupportInbox";
import { useSupportSearch } from "@/hooks/useSupportSearch";
import {
  envelopeText,
  type SupportView,
  type SupportChannel,
} from "@/lib/support-views";
import { assignTicket } from "@/lib/ticket-assignment";
import { supportSystemEventText } from "@/lib/support-system-events";
import {
  assignWarnings,
  buildAssignComment,
  setTicketStatus,
} from "@/lib/ticket-actions";
import { WorkspaceRail } from "./WorkspaceRail";
import { WorkspaceList } from "./WorkspaceList";
import type { DoneBroadcast } from "./WorkspaceList";
import {
  CONTACT_GROUPS,
  completedConversationBatches,
  isSnoozed,
  isCompleted,
  matchesWorkspace,
  type ContactGroup,
  type WorkspaceFilter,
  type WorkspaceTicket,
} from "@/lib/support-workspace";
import "./support-workspace.css";
import { TicketDetail } from "./TicketDetail";
import { NewConversationDialog } from "./NewConversationDialog";
import {
  SupportDesignListPreview,
  SupportDesignPreview,
  SupportDesignRailPreview,
} from "./SupportDesignPreview";

// Moved to src/lib/ticket-actions.ts (shared with the Brain deck); re-exported
// so existing imports and tests keep working.
export {
  assignWarnings,
  buildAssignComment,
  computeStatusPatch,
} from "@/lib/ticket-actions";

/** Breathing room kept below the inbox so the columns don't butt against the
 *  window edge. */
const INBOX_BOTTOM_GUTTER = 24;
/** Floor for short windows — below this the columns scroll rather than squash. */
const INBOX_MIN_HEIGHT = 560;
const ACTIVITY_PAGE_SIZE = 100;

type SupportActivityRow = {
  id: string;
  issue_id: string;
  user_id: string | null;
  comment: string;
  created_at: string;
  is_internal: boolean;
  is_system: boolean;
  sender_scope: string | null;
};

/**
 * Height for the three-column grid, measured rather than guessed.
 *
 * This was `h-[calc(100vh-220px)]`. Nothing owns that 220: it stood for the
 * sticky navbar plus the console's tab row and page padding, and it was wrong
 * by ~140px, so the columns stopped well short of the window bottom. A
 * constant like that also goes stale silently the next time the chrome above
 * changes. Measuring the grid's own offset can't drift.
 */
function useFillViewportHeight<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [height, setHeight] = useState<number | null>(null);

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    // Viewport-relative top: what we want is "from here to the bottom of the
    // window". Read it with the element's own height excluded from the
    // calculation (we set height, so measuring top only is stable and can't
    // feed back into itself).
    const top = el.getBoundingClientRect().top;
    setHeight(
      Math.max(INBOX_MIN_HEIGHT, window.innerHeight - top - INBOX_BOTTOM_GUTTER)
    );
  }, []);

  useLayoutEffect(() => {
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [measure]);

  return { ref, height };
}

export function SupportInbox() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { roster } = useAdminRoster();
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<"unread" | "urgent" | "overdue">("unread");
  const [selectionMode, setSelectionMode] = useState(false);
  const [group, setGroup] = useState<ContactGroup>("all");
  const [filter, setFilter] = useState<WorkspaceFilter>("all");
  const [done, setDone] = useState(false);
  const [newConversationOpen, setNewConversationOpen] = useState(false);
  const [newConversationSearch, setNewConversationSearch] = useState("");
  const [newConversationRecipient, setNewConversationRecipient] = useState<{
    recipientKind: "booking" | "lessor";
    recipientId: string;
  } | null>(null);
  const [focusedCommentId, setFocusedCommentId] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(() => {
    const state = location.state as { supportTicketId?: unknown } | null;
    return typeof state?.supportTicketId === "string"
      ? state.supportTicketId
      : null;
  });
  const designPreview =
    import.meta.env.DEV &&
    new URLSearchParams(location.search).get("supportPreview") === "v25";

  const { tickets, isLoading, error } = useSupportInbox({
    view: done ? "archived" : "all-open",
    channel: null,
    search: "",
    currentUserId: user?.id ?? null,
    includeCompleted: true,
  });
  const supportSearch = useSupportSearch({ query: search, group, filter });
  const q = supportSearch.query.toLowerCase();
  const classified: WorkspaceTicket[] = tickets.map((ticket) => ({
    ...ticket,
    // Every folder is viewed by one signed-in staff member. Keep badges tied
    // to that person's read marker; opening a ticket must not clear it for the
    // rest of the team merely because the folder is Snoozed or Overdue.
    unread_count: ticket.unread_count,
    contactGroup: ticket.contact_type ?? "external",
  }));
  const completedBatches = completedConversationBatches(classified);
  const completedBatchTicketIds = new Set(
    completedBatches.flatMap((batch) => batch.ticketIds)
  );
  const conversationBatchRows: DoneBroadcast[] = completedBatches.map(
    (batch) => ({
      id: batch.id,
      subject: batch.subject,
      sentAt: batch.sentAt,
      recipientLabel: t("support.broadcast.firstPlus", "{first} + {count}")
        .replace("{first}", batch.firstRecipient)
        .replace("{count}", String(batch.recipientCount - 1)),
    })
  );
  const unsearchedTickets = classified
    .filter(
      (ticket) =>
        !completedBatchTicketIds.has(ticket.id) &&
        isCompleted(ticket) === done &&
        (group === "all" || ticket.contactGroup === group) &&
        matchesWorkspace(ticket, filter, user?.id ?? null, Date.now())
    )
    .sort((a, b) => {
      const newest =
        Date.parse(
          b.latestMessageAt ?? b.last_message_at ?? b.created_at ?? ""
        ) -
        Date.parse(
          a.latestMessageAt ?? a.last_message_at ?? a.created_at ?? ""
        );
      if (sort === "urgent") {
        const priority =
          Number(b.priority === "urgent") - Number(a.priority === "urgent");
        return priority || newest;
      }
      if (sort === "overdue") {
        const threshold = Date.now() - 24 * 60 * 60 * 1000;
        const aTime = Date.parse(
          a.latestMessageAt ?? a.last_message_at ?? a.created_at ?? ""
        );
        const bTime = Date.parse(
          b.latestMessageAt ?? b.last_message_at ?? b.created_at ?? ""
        );
        const priority = Number(aTime > threshold) - Number(bTime > threshold);
        return priority || aTime - bTime;
      }
      return b.unread_count - a.unread_count || newest;
    });
  const ticketById = new Map(classified.map((ticket) => [ticket.id, ticket]));
  const visibleTickets = q
    ? supportSearch.conversationResults.flatMap((result) => {
        const ticket = ticketById.get(result.issue_id);
        return ticket
          ? [
              {
                ...ticket,
                searchSnippet: envelopeText(result.snippet),
                searchCommentId: result.comment_id,
              },
            ]
          : [];
      })
    : unsearchedTickets;

  const activityQuery = useInfiniteQuery({
    queryKey: ["support-team-activity", q],
    enabled: filter === "activity",
    initialPageParam: 0,
    queryFn: async ({ pageParam }) => {
      if (q) {
        const client = supabase as unknown as {
          rpc: (
            name: string,
            args: {
              search_query: string;
              result_limit: number;
              result_offset: number;
            }
          ) => Promise<{
            data: SupportActivityRow[] | null;
            error: { message: string } | null;
          }>;
        };
        const { data, error } = await client.rpc("search_support_activity", {
          search_query: q,
          result_limit: ACTIVITY_PAGE_SIZE,
          result_offset: pageParam,
        });
        if (error) throw new Error(error.message);
        return data ?? [];
      }
      const { data, error } = await supabase
        .from("issue_comments")
        .select(
          "id,issue_id,user_id,comment,created_at,is_internal,is_system,sender_scope"
        )
        .eq("sender_scope", "admin")
        .is("deleted_at", null)
        .order("created_at", { ascending: false })
        .order("id", { ascending: false })
        .range(pageParam, pageParam + ACTIVITY_PAGE_SIZE - 1);
      if (error) throw error;
      return (data ?? []) as SupportActivityRow[];
    },
    getNextPageParam: (lastPage, pages) =>
      lastPage.length === ACTIVITY_PAGE_SIZE
        ? pages.length * ACTIVITY_PAGE_SIZE
        : undefined,
  });
  const activityRows = activityQuery.data?.pages.flat() ?? [];
  const doneBroadcastsQuery = useQuery({
    queryKey: ["support-broadcasts", "done"],
    enabled: done,
    queryFn: async (): Promise<DoneBroadcast[]> => {
      const { data: ownOperator, error: ownOperatorError } = await supabase.rpc(
        "current_operator_id"
      );
      if (ownOperatorError) throw ownOperatorError;
      let operatorId = ownOperator ? String(ownOperator) : null;
      if (!operatorId) {
        const { data: platformOperator, error: platformOperatorError } =
          await supabase.rpc("platform_operator_id");
        if (platformOperatorError) throw platformOperatorError;
        operatorId = platformOperator ? String(platformOperator) : null;
      }
      if (!operatorId) return [];
      const { data: broadcasts, error: broadcastsError } = await supabase
        .from("broadcasts")
        .select("id,subject,recipient_count,sent_at")
        .eq("operator_id", operatorId)
        .not("sent_at", "is", null)
        .order("sent_at", { ascending: false })
        .limit(30);
      if (broadcastsError) throw broadcastsError;
      const ids = (broadcasts ?? []).map((row) => row.id);
      if (!ids.length) return [];
      const { data: recipients, error: recipientsError } = await supabase
        .from("broadcast_recipients")
        .select("broadcast_id,address")
        .in("broadcast_id", ids)
        .order("address", { ascending: true });
      if (recipientsError) throw recipientsError;
      const addresses = new Map<string, string[]>();
      for (const row of recipients ?? []) {
        const current = addresses.get(row.broadcast_id) ?? [];
        if (!current.includes(row.address)) current.push(row.address);
        addresses.set(row.broadcast_id, current);
      }
      return (broadcasts ?? []).map((broadcast) => {
        const unique = addresses.get(broadcast.id) ?? [];
        const total = unique.length || Number(broadcast.recipient_count || 0);
        const first =
          unique[0] ?? t("support.broadcast.recipients", "Recipients");
        return {
          id: broadcast.id,
          subject: broadcast.subject,
          sentAt: broadcast.sent_at!,
          recipientLabel:
            total > 1
              ? t("support.broadcast.firstPlus", "{first} + {count}")
                  .replace("{first}", first)
                  .replace("{count}", String(total - 1))
              : first,
        };
      });
    },
  });
  const railCounts: Record<string, number> = {};
  for (const g of CONTACT_GROUPS)
    railCounts[g.key] = classified.filter(
      (ticket) =>
        !isCompleted(ticket) &&
        !isSnoozed(ticket) &&
        (g.key === "all" || ticket.contactGroup === g.key)
    ).length;
  for (const f of [
    "overdue",
    "snoozed",
    ...roster.map((p) => `person:${p.id}`),
  ] as WorkspaceFilter[])
    railCounts[f] = classified.filter(
      (ticket) =>
        !isCompleted(ticket) &&
        matchesWorkspace(ticket, f, user?.id ?? null, Date.now())
    ).length;
  const folderTitle = filter.startsWith("person:")
    ? adminDisplayName(
        roster.find((p) => p.id === filter.slice(7)),
        t
      )
    : filter === "all"
      ? (() => {
          const g = CONTACT_GROUPS.find((g) => g.key === group)!;
          return t(`support.contact.${g.key}`, g.en);
        })()
      : (
          {
            mine: t("support.folder.mine", "Assigned to you"),
            activity: t("support.activity.title", "Team activity"),
            snoozed: t("support.rail.snoozed", "Snoozed"),
            overdue: t("support.rail.overdue", "Open over 24h"),
          } as Record<string, string>
        )[filter];

  const handleStatusChange = async (ticketId: string, status: string) => {
    const currentIndex = visibleTickets.findIndex(
      (ticket) => ticket.id === ticketId
    );
    const nextTicket =
      visibleTickets[currentIndex + 1] ??
      visibleTickets[currentIndex - 1] ??
      null;
    try {
      await setTicketStatus(ticketId, status);
    } catch (statusError: any) {
      toast.error(
        statusError?.message ||
          t("support.detail.statusUpdateFailed", "Failed to update status")
      );
      return;
    }
    const completed = ["resolved", "closed", "cancelled"].includes(status);
    if (completed && nextTicket) setSelectedId(nextTicket.id);
    toast.success(
      completed && nextTicket
        ? t("support.detail.doneNext", "Done · next: {name}").replace(
            "{name}",
            nextTicket.tenantName
          )
        : t("support.detail.statusUpdateSuccess", "Status updated")
    );
    queryClient.invalidateQueries({ queryKey: ["support-inbox"] });
    queryClient.invalidateQueries({
      queryKey: ["support-ticket-detail", ticketId],
    });
  };

  const handleAssign = async (
    ticketId: string,
    userId: string | null,
    note?: string
  ) => {
    const assigneeName = adminDisplayName(
      roster.find((a) => a.id === userId),
      t
    );
    // The database assignment trigger creates the assignee's in-app bell
    // notification. Handover deliberately sends no email and opens no dialog.
    const result = await assignTicket(supabase, {
      ticketId,
      assigneeId: userId,
      actorId: user?.id || "",
      systemComment: buildAssignComment(userId, roster, t, user?.id),
      assigneeName,
      note,
    });
    if (!result.assigned) {
      toast.error(
        result.errorMessage ||
          t("support.detail.assignFailed", "Failed to update assignee")
      );
      return false;
    }
    // The assignment already committed — nothing below rolls it back, each
    // failure only says what is missing.
    const warnings = assignWarnings(result, assigneeName, t);
    if (warnings.length) {
      for (const warning of warnings) toast.warning(warning);
    } else {
      toast.success(
        userId && userId !== user?.id
          ? t(
              "support.detail.assignedNotified",
              "{name} is now assigned and has been notified"
            ).replace("{name}", assigneeName)
          : t("support.detail.assignSuccess", "Assignee updated")
      );
    }
    queryClient.invalidateQueries({ queryKey: ["support-inbox"] });
    queryClient.invalidateQueries({
      queryKey: ["support-ticket-detail", ticketId],
    });
    // The assignee picker sits above BOTH tabs, so the Internal notes list can
    // be on screen when the note is written. IssueChatThread has no realtime
    // subscription; without this it would only show the note after a remount.
    // (["ticket-detail", "issue-comments", id] is its own key, TicketDetail
    // passes queryKeyPrefix="ticket-detail".)
    queryClient.invalidateQueries({
      queryKey: ["ticket-detail", "issue-comments", ticketId],
    });
    return true;
  };

  const { ref: gridRef, height: gridHeight } =
    useFillViewportHeight<HTMLDivElement>();

  const changeFolder = (next: {
    group?: ContactGroup;
    filter?: WorkspaceFilter;
  }) => {
    setSelectionMode(false);
    setGroup(next.group ?? "all");
    setFilter(next.filter ?? "all");
    setSelectedId(null);
  };
  const snooze = async (ticketId: string, until: string | null) => {
    const client = supabase as unknown as {
      rpc: (
        name: string,
        args: { ticket_id: string; until_at: string | null }
      ) => Promise<{ error: { message: string } | null }>;
    };
    const { error } = await client.rpc("snooze_support_ticket", {
      ticket_id: ticketId,
      until_at: until,
    });
    if (error) throw new Error(error.message);
    await queryClient.invalidateQueries({ queryKey: ["support-inbox"] });
    await queryClient.invalidateQueries({ queryKey: ["ticket-detail"] });
  };
  const bulkComplete = async (ids: string[]) => {
    try {
      await Promise.all(
        ids.map((id) => setTicketStatus(id, done ? "open" : "resolved"))
      );
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t("support.list.bulkCompleteFailed", "Could not complete tickets")
      );
      throw error;
    } finally {
      void queryClient.invalidateQueries({ queryKey: ["support-inbox"] });
      void queryClient.invalidateQueries({
        queryKey: ["support-ticket-detail"],
      });
    }
  };
  const bulkAssign = async (ids: string[], assignee: string) => {
    for (const id of ids)
      if (!(await handleAssign(id, assignee)))
        throw new Error(t("support.detail.assignFailed", "Assignment failed"));
  };

  useEffect(() => {
    if (filter === "activity") return;
    if (!visibleTickets.length) {
      setSelectedId(null);
      return;
    }
    if (
      !selectedId ||
      !visibleTickets.some((ticket) => ticket.id === selectedId)
    ) {
      setSelectedId(visibleTickets[0].id);
    }
  }, [filter, selectedId, visibleTickets]);

  return (
    <div
      ref={gridRef}
      style={gridHeight ? { height: gridHeight } : undefined}
      className="support-workspace"
      data-ticket-open={!!selectedId}
    >
      {designPreview ? (
        <SupportDesignRailPreview />
      ) : (
        <WorkspaceRail
          currentUserId={user?.id}
          group={group}
          filter={filter}
          counts={railCounts}
          roster={roster}
          onGroup={(group) => changeFolder({ group })}
          onFilter={(filter) => changeFolder({ filter })}
          onCompose={() => {
            setNewConversationSearch("");
            setNewConversationRecipient(null);
            setNewConversationOpen(true);
          }}
        />
      )}
      {newConversationOpen && (
        <NewConversationDialog
          open
          onOpenChange={setNewConversationOpen}
          tickets={classified}
          initialSearch={newConversationSearch}
          initialRecipient={newConversationRecipient}
          onStarted={(issueId) => {
            queryClient.invalidateQueries({ queryKey: ["support-inbox"] });
            setDone(false);
            setSelectedId(issueId);
            setNewConversationSearch("");
            setNewConversationRecipient(null);
          }}
        />
      )}
      <section
        className={`support-list-pane${designPreview ? " is-design-preview" : ""}`}
        aria-label={t("support.list.messages", "Messages")}
      >
        {designPreview && <SupportDesignListPreview />}
        <div className="support-list-heading">
          <div className="flex items-center gap-2">
            <h1 className="min-w-0 flex-1 truncate font-semibold">
              {folderTitle}
            </h1>
            <button
              className="support-text-button"
              onClick={() => setSelectionMode(!selectionMode)}
            >
              {selectionMode
                ? t("common.cancel", "Cancel")
                : t("support.list.select", "Select")}
            </button>
            <div
              className="support-segment"
              role="group"
              aria-label={t("support.list.ticketState", "Ticket state")}
            >
              {[false, true].map((value) => (
                <button
                  key={String(value)}
                  aria-pressed={done === value}
                  onClick={() => {
                    setSelectionMode(false);
                    setDone(value);
                    setSelectedId(null);
                  }}
                >
                  {value
                    ? t("support.list.done", "Done")
                    : t("support.list.open", "Open")}
                </button>
              ))}
            </div>
          </div>
          <label className="support-search">
            <Search size={14} />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("common.search", "Search")}
              aria-label={t("support.list.searchMessages", "Search messages")}
            />
          </label>
          <select
            className="support-sort"
            aria-label={t("support.list.sort", "Sort messages")}
            value={sort}
            onChange={(event) => setSort(event.target.value as typeof sort)}
          >
            <option value="unread">
              {t("support.list.sortUnread", "Unread first")}
            </option>
            <option value="urgent">
              {t("support.list.sortUrgent", "Urgent first")}
            </option>
            <option value="overdue">
              {t("support.list.sortOverdue", "Open over 24h")}
            </option>
          </select>
          <div className="support-mobile-folders">
            <select
              aria-label={t("support.rail.inbox", "Inbox")}
              value={filter === "all" ? group : filter}
              onChange={(e) =>
                CONTACT_GROUPS.some((g) => g.key === e.target.value)
                  ? changeFolder({ group: e.target.value as ContactGroup })
                  : changeFolder({ filter: e.target.value as WorkspaceFilter })
              }
            >
              {CONTACT_GROUPS.map((g) => (
                <option key={g.key} value={g.key}>
                  {t(`support.contact.${g.key}`, g.en)}
                </option>
              ))}
              {roster.map((p) => (
                <option key={p.id} value={`person:${p.id}`}>
                  {supportRosterLabel(p, t)}
                </option>
              ))}
              <option value="activity">
                {t("support.activity.title", "Team activity")}
              </option>
              <option value="snoozed">
                {t("support.rail.snoozed", "Snoozed")}
              </option>
              <option value="overdue">
                {t("support.rail.overdue", "Open over 24h")}
              </option>
            </select>
            <button
              className="support-primary"
              onClick={() => {
                setNewConversationSearch("");
                setNewConversationRecipient(null);
                setNewConversationOpen(true);
              }}
            >
              {t("support.rail.new", "New")}
            </button>
          </div>
        </div>
        {supportSearch.error && (
          <p role="alert" className="p-3 text-xs text-destructive">
            {t(
              "support.list.partialSearchError",
              "Some filters could not be loaded."
            )}
          </p>
        )}
        {error ? (
          <div role="alert" className="support-empty">
            {t(
              "support.list.loadError",
              "Couldn't load tickets. Reload to retry."
            )}
          </div>
        ) : isLoading || (q && supportSearch.isLoading) ? (
          <div role="status" className="support-empty">
            {t("common.loading", "Loading…")}
          </div>
        ) : filter === "activity" ? (
          <div
            className="min-h-0 flex-1 overflow-y-auto py-1"
            aria-label={t("support.activity.title", "Team activity")}
          >
            {activityQuery.isLoading ? (
              <div className="support-empty">
                {t("common.loading", "Loading…")}
              </div>
            ) : null}
            {activityQuery.error ? (
              <div className="support-empty">
                {t("support.activity.error", "Couldn't load team activity")}
              </div>
            ) : null}
            {activityRows.map((item) => {
              const actor = adminDisplayName(
                roster.find((person) => person.id === item.user_id),
                t
              );
              const ticket = classified.find(
                (candidate) => candidate.id === item.issue_id
              );
              return (
                <button
                  key={item.id}
                  type="button"
                  className="support-activity-row"
                  onClick={() => {
                    setSelectedId(item.issue_id);
                    setFocusedCommentId(item.id);
                  }}
                >
                  <strong>{actor}</strong>
                  <span>
                    {item.is_system
                      ? (supportSystemEventText(
                          item.comment,
                          item.sender_scope as "admin" | "tenant" | null,
                          null,
                          true,
                          t
                        ) ?? item.comment)
                      : item.is_internal
                        ? t("support.activity.noted", "added an internal note")
                        : t("support.activity.replied", "replied")}
                  </span>
                  {ticket ? (
                    <small>
                      {ticket.tenantName} · {ticket.title}
                    </small>
                  ) : null}
                </button>
              );
            })}
            {activityQuery.hasNextPage ? (
              <button
                type="button"
                className="support-text-button mx-auto my-3 block"
                disabled={activityQuery.isFetchingNextPage}
                onClick={() => void activityQuery.fetchNextPage()}
              >
                {activityQuery.isFetchingNextPage
                  ? t("common.loading", "Loading…")
                  : t("support.search.more", "Load more")}
              </button>
            ) : null}
          </div>
        ) : (
          <>
            {q && supportSearch.contacts.length ? (
              <div className="support-search-contacts">
                <strong>{t("support.search.contacts", "Contacts")}</strong>
                {supportSearch.contacts.map((contact) => (
                  <div
                    key={`${contact.recipient_kind}:${contact.recipient_id}`}
                    className="support-search-contact"
                  >
                    <span className="min-w-0">
                      <b className="block truncate">{contact.contact_name}</b>
                      <small className="block truncate">
                        {contact.contact_detail}
                      </small>
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        setNewConversationSearch(contact.contact_name);
                        setNewConversationRecipient({
                          recipientKind: contact.recipient_kind,
                          recipientId: contact.recipient_id,
                        });
                        setNewConversationOpen(true);
                      }}
                    >
                      {t("support.new.title", "New message")}
                    </button>
                  </div>
                ))}
              </div>
            ) : null}
            <WorkspaceList
              key={`${group}:${filter}:${done}:${selectionMode}`}
              tickets={visibleTickets}
              broadcasts={
                done && !q
                  ? [
                      ...conversationBatchRows,
                      ...(doneBroadcastsQuery.data ?? []),
                    ]
                  : []
              }
              selectedId={selectedId}
              onSelect={(id, commentId) => {
                setSelectedId(id);
                setFocusedCommentId(commentId ?? null);
              }}
              completed={done}
              onComplete={bulkComplete}
              onSnooze={snooze}
              onAssign={bulkAssign}
              roster={roster}
              selectionMode={selectionMode}
              onClearSelection={() => setSelectionMode(false)}
              searchTerm={q}
            />
            {q && supportSearch.hasMore ? (
              <button
                type="button"
                className="support-search-more"
                disabled={supportSearch.isLoadingMore}
                onClick={() => void supportSearch.loadMore()}
              >
                {supportSearch.isLoadingMore
                  ? t("common.loading", "Loading…")
                  : t("support.search.more", "Load more")}
              </button>
            ) : null}
          </>
        )}
      </section>
      <section
        className="support-detail-pane"
        aria-label={t("support.detail.conversation", "Conversation")}
      >
        <button className="support-back" onClick={() => setSelectedId(null)}>
          <ArrowLeft size={14} />
          {t("common.back", "Back")}
        </button>
        {designPreview ? (
          <SupportDesignPreview />
        ) : (
          <TicketDetail
            ticketId={selectedId}
            focusCommentId={focusedCommentId}
            supportAppearance
            onStatusChange={
              selectedId
                ? (status) => handleStatusChange(selectedId, status)
                : undefined
            }
            onAssign={
              selectedId
                ? async (userId) => {
                    await handleAssign(selectedId, userId);
                  }
                : undefined
            }
            onConversationSent={() => {
              const currentIndex = visibleTickets.findIndex(
                (ticket) => ticket.id === selectedId
              );
              const candidates = [
                ...visibleTickets.slice(currentIndex + 1),
                ...visibleTickets.slice(0, Math.max(currentIndex, 0)),
              ];
              const nextTicket =
                candidates.find(
                  (ticket) =>
                    !isCompleted(ticket) &&
                    ticket.status !== "waiting" &&
                    !isSnoozed(ticket)
                ) ?? null;
              if (nextTicket && nextTicket.id !== selectedId)
                setSelectedId(nextTicket.id);
            }}
          />
        )}
      </section>
    </div>
  );
}
